import React, { useEffect, useRef } from 'react';
import { motion, useAnimation, useInView } from 'framer-motion';
import './TermsConditions.css';
import Footer from '../Home/Footer';

const TermsConditions = () => {
  const containerRef = useRef(null);
  const isInView = useInView(containerRef, { once: true, amount: 0.1 });
  const controls = useAnimation();

  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = "Terms and Conditions | Your Company Name";
    
    if (isInView) {
      controls.start("visible");
    }

    const handlePopState = () => {
      window.scrollTo(0, 0);
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, [controls, isInView]);

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.08,
        when: "beforeChildren"
      }
    }
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        type: "spring",
        stiffness: 100,
        damping: 10,
        duration: 0.5
      }
    },
    hover: {
      scale: 1.01,
      transition: { duration: 0.2 }
    }
  };

  const fadeInVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { duration: 0.8 }
    }
  };




  const sections = [
    {
      title: "Enrollment and Payment",
      content: [
        "You may need to create an account and enroll your child in the course.",
        "Payment terms, including fees, payment methods, and refund policies, should be clearly stated."
      ]
    },
    {
      title: "Access and Duration",
      content: [
        "Information about the duration of the course, access periods, and any expiration dates for course materials will be provided."
      ]
    },
    {
      title: "Age Restrictions",
      content: [
        "Some courses may have age restrictions or recommended age ranges to ensure the content is appropriate for the child's skill level."
      ]
    },
    {
      title: "Privacy and Data Collection",
      content: [
        "The platform's privacy policy details how personal data and information will be collected, used, and protected."
      ]
    },
    {
      title: "Code of Conduct",
      content: [
        "Expectations for student behavior, including guidelines against plagiarism, cheating, or any form of disruptive behavior."
      ]
    },
    {
      title: "Intellectual Property",
      content: [
        "Information about who owns the course content and any restrictions on using or sharing course materials."
      ]
    },
    {
      title: "Technical Requirements",
      content: [
        "Information on the technical requirements, such as the required hardware, software, and internet connection speed."
      ]
    },
    {
      title: "Support and Communication",
      content: [
        "Details on how students and parents can communicate with instructors or support staff for questions and assistance."
      ]
    },
    {
      title: "Progress Tracking and Reporting",
      content: [
        "Information about how the child's progress will be tracked, assessed, and reported, including any certifications or badges earned."
      ]
    },
    {
      title: "Termination and Suspension",
      content: [
        "Circumstances under which the platform can terminate or suspend a student's access to the course, such as violating the code of conduct."
      ]
    },
    {
      title: "Dispute Resolution",
      content: [
        "Procedures for resolving disputes or issues between the platform and the student or parent."
      ]
    },
    {
      title: "Changes to Terms and Conditions",
      content: [
        "Information about the platform's right to modify the terms and conditions and how such changes will be communicated to users."
      ]
    },
    {
      title: "Liability and Disclaimer",
      content: [
        "A disclaimer outlining the platform's limitations of liability and any warranties or guarantees associated with the course."
      ]
    },
    {
      title: "Governing Law",
      content: [
        "The jurisdiction and laws that govern the terms and conditions."
      ]
    },
    {
      title: "Feedback and Testimonials",
      content: [
        "Information on how feedback and testimonials provided by students or parents may be used by the platform."
      ]
    }
  ];

  return (
    <div className="legal-document">
      <motion.div 
        ref={containerRef}
        className="terms-container"
        initial="hidden"
        animate={controls}
        variants={containerVariants}
      >
        <motion.header 
          className="legal-header"
          variants={fadeInVariants}
          whileHover={{ scale: 1.02 }}
        >
          <motion.h1 
            variants={itemVariants}
            whileHover={{ color: "#2c5282" }}
          >
            Terms and Conditions
          </motion.h1>
          <motion.p 
            className="effective-date"
            variants={itemVariants}
            whileHover={{ scale: 1.05 }}
          >
            Effective Date: {new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
          </motion.p>
        </motion.header>

        <motion.div 
          className="legal-content" 
          variants={containerVariants}
        >
          {sections.map((section, index) => (
            <motion.article 
              key={index} 
              className="legal-section"
              variants={itemVariants}
              whileHover="hover"
              initial="hidden"
              animate={controls}
            >
              <motion.h2
                whileHover={{ x: 5, color: "#2b6cb0" }}
              >
                {section.title}
              </motion.h2>
              <motion.div 
                className="legal-text"
                variants={containerVariants}
              >
                {section.content.map((paragraph, pIndex) => (
                  <motion.p 
                    key={pIndex}
                    variants={itemVariants}
                    whileHover={{ x: 3 }}
                  >
                    {paragraph}
                  </motion.p>
                ))}
              </motion.div>
            </motion.article>
          ))}
        </motion.div>

        <motion.div 
          className="legal-footer"
          variants={fadeInVariants}
          // whileHover={{ backgroundColor: "#f7fafc" }}
        >
          <motion.p whileHover={{ scale: 1.01 }}>
            If you have any questions about these Terms, please contact us at support@ai4bazaar.com.
          </motion.p>
          <motion.p 
            className="copyright"
            whileHover={{ scale: 1.02 }}
          >
            © {new Date().getFullYear()} <b>VISYS CLOUD TECHNOLOGIES INDIA.</b> All rights reserved.
          </motion.p>
        </motion.div>
      </motion.div>
      <Footer />
    </div>
  );
};

export default TermsConditions;